import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { Plus, Ban } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { ApiKeyForm } from "./components/api-key-form"
import { ApiKeyCreatedDialog } from "./components/api-key-created-dialog"
import { ApiKeysQuery } from "@/lib/graphql/queries"
import { RevokeApiKeyMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type ApiKeyItem = ResultOf<typeof ApiKeysQuery>["apiKeys"]["nodes"][number]

const LIMIT = 25

function KeyStatus({ row }: { row: ApiKeyItem }) {
  if (row.revokedAt) {
    return <Badge variant="destructive" className="text-2xs">Revoked</Badge>
  }
  if (row.expiresAt && new Date(row.expiresAt) < new Date()) {
    return <Badge variant="outline" className="text-2xs border-amber-500/50 text-amber-400">Expired</Badge>
  }
  return <Badge variant="outline" className="text-2xs border-emerald-500/50 text-emerald-400">Active</Badge>
}

export function ApiKeysPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const search = debouncedSearch || undefined
  const offset = Number(searchParams.get("offset") ?? "0")

  const [result, reexecute] = useQuery({
    query: ApiKeysQuery,
    variables: { search, limit: LIMIT, offset },
  })
  const [, revokeApiKey] = useMutation(RevokeApiKeyMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [revokeId, setRevokeId] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)

  const handleRevoke = async () => {
    if (!revokeId) return
    setRevoking(true)
    const res = await revokeApiKey({ id: revokeId })
    setRevoking(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("API key revoked")
    setRevokeId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleKeyCreated = (rawKey: string) => {
    setCreatedKey(rawKey)
    reexecute({ requestPolicy: "network-only" })
  }

  const columns: Column<ApiKeyItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Key Prefix",
      cell: (row) => <code className="text-xs font-mono text-muted-foreground">{row.keyPrefix}...</code>,
    },
    {
      header: "Scopes",
      cell: (row) =>
        row.scopes.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {row.scopes.map((scope) => (
              <Badge key={scope} variant="secondary" className="text-2xs">
                {scope}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">all</span>
        ),
    },
    {
      header: "Status",
      cell: (row) => <KeyStatus row={row} />,
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "Last Used",
      cell: (row) =>
        row.lastUsedAt ? (
          <TimeAgo date={row.lastUsedAt} />
        ) : (
          <span className="text-xs text-muted-foreground">never</span>
        ),
    },
    {
      header: "Expires",
      cell: (row) =>
        row.expiresAt ? (
          <TimeAgo date={row.expiresAt} />
        ) : (
          <span className="text-xs text-muted-foreground">never</span>
        ),
    },
    {
      header: "",
      cell: (row) =>
        !row.revokedAt ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            aria-label="Revoke"
            onClick={(e) => {
              e.stopPropagation()
              setRevokeId(row.id)
            }}
          >
            <Ban className="h-3.5 w-3.5" />
          </Button>
        ) : null,
      className: "text-right",
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="API Keys" description="Manage API keys for programmatic access">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create API Key
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search keys..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-60 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.apiKeys.nodes ?? []}
        keyFn={(row) => row.id}
        pageInfo={result.data?.apiKeys.pageInfo}
        onPageChange={(o) => {
          const params = new URLSearchParams(searchParams)
          params.set("offset", String(o))
          setSearchParams(params, { replace: true })
        }}
        loading={result.fetching}
        emptyTitle="No API keys"
        emptyDescription="Create an API key to get started"
      />

      <ApiKeyForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleKeyCreated}
      />

      <ApiKeyCreatedDialog
        open={!!createdKey}
        onOpenChange={(open) => { if (!open) setCreatedKey(null) }}
        rawKey={createdKey ?? ""}
      />

      <ConfirmDialog
        open={!!revokeId}
        onOpenChange={(open) => { if (!open) setRevokeId(null) }}
        title="Revoke API Key"
        description="This will permanently revoke this API key. Any applications using it will lose access."
        confirmLabel="Revoke"
        onConfirm={handleRevoke}
        loading={revoking}
        destructive
      />
    </div>
  )
}
